import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Spin, Input, Button, message, Tag, Tooltip, Modal, InputNumber, Drawer, Checkbox, Select, Badge, Divider } from 'antd';
import {
  SendOutlined,
  PieChartOutlined,
  LineChartOutlined,
  BarChartOutlined,
  DotChartOutlined,
  HeatMapOutlined,
  BoxPlotOutlined,
  FundOutlined,
  RadarChartOutlined,
  FunnelPlotOutlined,
  AreaChartOutlined,
  StockOutlined,
  AppstoreOutlined,
  LockOutlined,
  ArrowLeftOutlined,
  ControlOutlined,
  ExperimentOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { recommendCharts, getColumns } from '../services/api';
import { CATEGORIES } from '../utils/chartUtils';
import type { ColumnInfo } from '../types';

interface ChartInfo {
  chart_type: string;
  display_name: string;
  icon: string;
  confidence: number;
  reason: string;
  compatible: boolean;
  require: string;
}

const iconMap: Record<string, React.ReactNode> = {
  pie: <PieChartOutlined />,
  line: <LineChartOutlined />,
  bar: <BarChartOutlined />,
  scatter: <DotChartOutlined />,
  bubble: <DotChartOutlined />,
  heatmap: <HeatMapOutlined />,
  boxplot: <BoxPlotOutlined />,
  histogram: <BarChartOutlined />,
  pareto: <FundOutlined />,
  radar: <RadarChartOutlined />,
  funnel: <FunnelPlotOutlined />,
  area: <AreaChartOutlined />,
  treemap: <AppstoreOutlined />,
  candlestick: <StockOutlined />,
  // Industrial / statistical chart types
  i_mr: <ControlOutlined />,
  xbar_r: <ControlOutlined />,
  xbar_s: <ControlOutlined />,
  p_chart: <ControlOutlined />,
  np_chart: <ControlOutlined />,
  c_chart: <ControlOutlined />,
  u_chart: <ControlOutlined />,
  ewma: <ControlOutlined />,
  cusum: <ControlOutlined />,
  capability_hist: <ExperimentOutlined />,
  qq_plot: <DotChartOutlined />,
  correlation_matrix: <HeatMapOutlined />,
  main_effects: <FundOutlined />,
  interaction_plot: <FundOutlined />,
  // New statistical analysis chart types
  regression_scatter: <DotChartOutlined />,
  feature_importance: <BarChartOutlined />,
  residual_plot: <DotChartOutlined />,
  effects_pareto: <BarChartOutlined />,
  response_surface: <HeatMapOutlined />,
  variance_components: <BarChartOutlined />,
  weibull_plot: <DotChartOutlined />,
  reliability_curve: <LineChartOutlined />,
  pca_scree: <BarChartOutlined />,
  pca_biplot: <DotChartOutlined />,
  cluster_scatter: <DotChartOutlined />,
  distribution_fit: <BarChartOutlined />,
  forecast_plot: <LineChartOutlined />,
  seasonal_decomp: <LineChartOutlined />,
  time_series: <LineChartOutlined />,
};

const TYPE_TAG_COLORS: Record<string, string> = {
  float: 'blue',
  integer: 'cyan',
  categorical: 'green',
  datetime: 'orange',
  text: 'default',
};

const SmartChartTypeSelect: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const rawParams = searchParams.get('params') || '{}';
  const params = useMemo(() => {
    try {
      return JSON.parse(decodeURIComponent(rawParams));
    } catch {
      return {};
    }
  }, [rawParams]);

  const [loading, setLoading] = useState(true);
  const [top3, setTop3] = useState<ChartInfo[]>([]);
  const [allCharts, setAllCharts] = useState<ChartInfo[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [specModalOpen, setSpecModalOpen] = useState(false);
  const [uslValue, setUslValue] = useState<number | null>(null);
  const [lslValue, setLslValue] = useState<number | null>(null);

  // Drawer state for column selection
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [allColumns, setAllColumns] = useState<ColumnInfo[]>([]);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [selectedCols, setSelectedCols] = useState<string[]>(params.columns || []);
  const [xAxis, setXAxis] = useState<string>(params.xAxis || '');
  const [yAxis, setYAxis] = useState<string>(params.yAxis || '');
  const [groupBy, setGroupBy] = useState<string>(params.groupBy || '');

  // Fetch recommendations
  const fetchRecommendations = useCallback(async (columns: string[]) => {
    setLoading(true);
    try {
      const res = await recommendCharts(params.fileId, columns);
      if (res.top3 && res.all_charts) {
        setTop3(res.top3);
        setAllCharts(res.all_charts);
      } else if (Array.isArray(res)) {
        setTop3(res);
        setAllCharts([]);
      }
    } catch {
      setTop3([
        { chart_type: 'bar', display_name: '柱状图', icon: 'bar', confidence: 0.92, reason: '适合展示分类数据的数值对比', compatible: true, require: '' },
        { chart_type: 'line', display_name: '折线图', icon: 'line', confidence: 0.85, reason: '适合展示数据的变化趋势', compatible: true, require: '' },
        { chart_type: 'scatter', display_name: '散点图', icon: 'scatter', confidence: 0.78, reason: '适合探索变量之间的关系', compatible: true, require: '' },
      ]);
    } finally {
      setLoading(false);
    }
  }, [params.fileId]);

  useEffect(() => {
    fetchRecommendations(params.columns || []);
  }, []);

  // Fetch all columns when drawer opens for the first time
  const handleOpenDrawer = useCallback(async () => {
    setDrawerOpen(true);
    if (allColumns.length === 0 && params.fileId) {
      setColumnsLoading(true);
      try {
        const cols = await getColumns(params.fileId);
        setAllColumns(cols);
      } catch {
        message.error('获取列信息失败');
      } finally {
        setColumnsLoading(false);
      }
    }
  }, [allColumns.length, params.fileId]);

  // Apply column changes: update URL params and refresh recommendations
  const handleApplyColumns = useCallback(() => {
    if (selectedCols.length === 0) {
      message.warning('请至少选择一列');
      return;
    }
    const newParams = {
      ...params,
      columns: selectedCols,
      ...(xAxis ? { xAxis } : {}),
      ...(yAxis ? { yAxis } : {}),
      ...(groupBy ? { groupBy } : {}),
    };
    // Remove empty keys
    if (!xAxis) delete newParams.xAxis;
    if (!yAxis) delete newParams.yAxis;
    if (!groupBy) delete newParams.groupBy;

    const encoded = encodeURIComponent(JSON.stringify(newParams));
    setSearchParams({ params: encoded });
    setDrawerOpen(false);
    fetchRecommendations(selectedCols);
    message.success(`已更新为 ${selectedCols.length} 列，正在刷新推荐...`);
  }, [selectedCols, xAxis, yAxis, groupBy, params, setSearchParams, fetchRecommendations]);

  const handleSelectChart = (chart: ChartInfo) => {
    if (!chart.compatible) {
      message.warning(`${chart.display_name}不适用于当前数据：${chart.require}`);
      return;
    }
    if (chart.chart_type === 'capability_hist') {
      setSpecModalOpen(true);
      return;
    }
    let currentParams: Record<string, any> = {};
    try { currentParams = JSON.parse(decodeURIComponent(searchParams.get('params') || '{}')); } catch { /* use empty */ }
    const newParams = encodeURIComponent(
      JSON.stringify({ ...currentParams, chartType: chart.chart_type })
    );
    navigate(`/smart-chart/result?params=${newParams}`);
  };

  const handleSpecConfirm = () => {
    setSpecModalOpen(false);
    const opts: Record<string, any> = {};
    if (uslValue !== null) opts.usl = uslValue;
    if (lslValue !== null) opts.lsl = lslValue;
    let currentParams: Record<string, any> = {};
    try { currentParams = JSON.parse(decodeURIComponent(searchParams.get('params') || '{}')); } catch { /* use empty */ }
    const newParams = encodeURIComponent(
      JSON.stringify({ ...currentParams, chartType: 'capability_hist', options: opts })
    );
    navigate(`/smart-chart/result?params=${newParams}`);
  };

  const handleChat = () => {
    if (!chatInput.trim()) return;
    const input = chatInput.trim().toLowerCase();

    // Keyword matching for chart types
    const chartKeywordMap: Array<{ keywords: string[]; chartType: string; label: string }> = [
      { keywords: ['饼', '占比', '比例', '份额', '构成'], chartType: 'pie', label: '饼图' },
      { keywords: ['时间序列', 'time series', '时间趋势', '日期趋势', '时序'], chartType: 'time_series', label: '时间序列图' },
      { keywords: ['折线', '趋势', '变化', '走势', '时间'], chartType: 'line', label: '折线图' },
      { keywords: ['柱', '条', '对比', '比较', '排名'], chartType: 'bar', label: '柱状图' },
      { keywords: ['散点', '相关', '关系', '分布'], chartType: 'scatter', label: '散点图' },
      { keywords: ['气泡'], chartType: 'bubble', label: '气泡图' },
      { keywords: ['热力', '热图', 'heatmap'], chartType: 'heatmap', label: '热力图' },
      { keywords: ['箱线', '箱型', '异常值', 'boxplot'], chartType: 'boxplot', label: '箱线图' },
      { keywords: ['直方', '频率', '频次', 'histogram'], chartType: 'histogram', label: '直方图' },
      { keywords: ['帕累托', 'pareto', '二八'], chartType: 'pareto', label: '帕累托图' },
      { keywords: ['雷达', 'radar', '多维'], chartType: 'radar', label: '雷达图' },
      { keywords: ['漏斗', 'funnel', '转化'], chartType: 'funnel', label: '漏斗图' },
      { keywords: ['面积', 'area'], chartType: 'area', label: '面积图' },
      { keywords: ['树图', 'treemap', '层级'], chartType: 'treemap', label: '矩形树图' },
      { keywords: ['k线', '蜡烛', 'candlestick', '股票'], chartType: 'candlestick', label: 'K线图' },
      // Industrial / statistical chart types
      { keywords: ['i-mr', 'imr', '单值', '移动极差', '控制图'], chartType: 'i_mr', label: 'I-MR 控制图' },
      { keywords: ['xbar-r', 'xbar_r', '均值极差'], chartType: 'xbar_r', label: 'Xbar-R 控制图' },
      { keywords: ['xbar-s', 'xbar_s', '均值标准差'], chartType: 'xbar_s', label: 'Xbar-S 控制图' },
      { keywords: ['xbar', 'x-bar', '子组'], chartType: 'xbar_r', label: 'Xbar-R 控制图' },
      { keywords: ['p图', 'p控制', '不合格率'], chartType: 'p_chart', label: 'P 控制图' },
      { keywords: ['np', '不合格品数'], chartType: 'np_chart', label: 'NP 控制图' },
      { keywords: ['c图', 'c控制', '缺陷数'], chartType: 'c_chart', label: 'C 控制图' },
      { keywords: ['u图', 'u控制', '单位缺陷'], chartType: 'u_chart', label: 'U 控制图' },
      { keywords: ['ewma', '指数加权', '小偏移'], chartType: 'ewma', label: 'EWMA 控制图' },
      { keywords: ['cusum', '累积和', '持续偏移'], chartType: 'cusum', label: 'CUSUM 控制图' },
      { keywords: ['能力直方', 'cpk', '过程能力', '规格限'], chartType: 'capability_hist', label: '能力直方图' },
      { keywords: ['qq', '正态概率', '正态检验'], chartType: 'qq_plot', label: 'QQ 正态概率图' },
      { keywords: ['相关矩阵', '相关系数', '皮尔逊'], chartType: 'correlation_matrix', label: '相关矩阵图' },
      { keywords: ['主效应', 'main effect'], chartType: 'main_effects', label: '主效应图' },
      { keywords: ['交互作用', 'interaction'], chartType: 'interaction_plot', label: '交互作用图' },
      // New statistical analysis chart types
      { keywords: ['回归', 'regression', '拟合线', '线性回归'], chartType: 'regression_scatter', label: '回归散点图' },
      { keywords: ['特征重要性', 'feature importance', '变量重要性', '随机森林'], chartType: 'feature_importance', label: '特征重要性图' },
      { keywords: ['残差', 'residual', '模型诊断'], chartType: 'residual_plot', label: '残差分析图' },
      { keywords: ['效应帕累托', '效应排序', '显著因子', 'effects pareto'], chartType: 'effects_pareto', label: '效应帕累托图' },
      { keywords: ['响应曲面', 'response surface', '3d', '曲面'], chartType: 'response_surface', label: '3D 响应曲面' },
      { keywords: ['方差分量', 'variance component', 'grr', 'gage', '测量系统'], chartType: 'variance_components', label: '方差分量图' },
      { keywords: ['weibull', '威布尔', '寿命分布'], chartType: 'weibull_plot', label: 'Weibull 概率图' },
      { keywords: ['可靠度', '可靠性', 'reliability', 'b10'], chartType: 'reliability_curve', label: '可靠度函数图' },
      { keywords: ['碎石图', 'scree', '主成分数'], chartType: 'pca_scree', label: 'PCA 碎石图' },
      { keywords: ['双标图', 'biplot', '载荷图'], chartType: 'pca_biplot', label: 'PCA 双标图' },
      { keywords: ['聚类', 'cluster', 'kmeans', '分群'], chartType: 'cluster_scatter', label: '聚类散点图' },
      { keywords: ['分布拟合', 'distribution fit', '拟合优度'], chartType: 'distribution_fit', label: '分布拟合图' },
      { keywords: ['预测', 'forecast', '外推', '趋势预测'], chartType: 'forecast_plot', label: '趋势预测图' },
      { keywords: ['季节分解', 'seasonal', '周期分解', '趋势分解'], chartType: 'seasonal_decomp', label: '季节分解图' },
    ];

    let matched: { chartType: string; label: string } | null = null;
    for (const entry of chartKeywordMap) {
      if (entry.keywords.some((kw) => input.includes(kw))) {
        matched = { chartType: entry.chartType, label: entry.label };
        break;
      }
    }

    if (matched) {
      // Find the chart in top3 or allCharts to check compatibility
      const allAvailable = [...top3, ...allCharts];
      const chartInfo = allAvailable.find(c => c.chart_type === matched!.chartType);

      if (chartInfo && !chartInfo.compatible) {
        message.warning(`${matched.label}不适用于当前数据：${chartInfo.require}`);
      } else {
        message.success(`已匹配到「${matched.label}」，正在跳转...`);
        let currentParams: Record<string, any> = {};
    try { currentParams = JSON.parse(decodeURIComponent(searchParams.get('params') || '{}')); } catch { /* use empty */ }
        const newParams = encodeURIComponent(
          JSON.stringify({ ...currentParams, chartType: matched.chartType })
        );
        navigate(`/smart-chart/result?params=${newParams}`);
      }
    } else {
      message.info('未能识别图表类型，请尝试使用关键词，如：饼图、折线图、柱状图、散点图等');
    }

    setChatInput('');
  };

  // MECE categories - use shared CATEGORIES from chartUtils
  const LAYOUT_CONFIG: Record<string, { gridCols: number; layout: 'grid' | 'card' }> = {
    overview: { gridCols: 5, layout: 'grid' },
    trend: { gridCols: 5, layout: 'grid' },
    relation: { gridCols: 4, layout: 'grid' },
    compare: { gridCols: 3, layout: 'card' },
    spc: { gridCols: 3, layout: 'card' },
    explore: { gridCols: 4, layout: 'grid' },
  };

  const categorizedCharts = CATEGORIES.map(cat => ({
    ...cat,
    ...(LAYOUT_CONFIG[cat.key] || { gridCols: 4, layout: 'grid' as const }),
    charts: allCharts.filter(c => c.compatible && cat.types.has(c.chart_type)),
  }));
  const incompatibleCharts = allCharts.filter(c => !c.compatible);

  // Column select helpers
  const numericCols = allColumns.filter(c => c.dtype === 'float64' || c.dtype === 'int64' || c.dtype === 'float' || c.dtype === 'integer');
  const categoricalCols = allColumns.filter(c => c.dtype === 'object' || c.dtype === 'categorical' || c.dtype === 'text');
  const colOptions = selectedCols.map(c => ({ value: c, label: c }));

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '20px 0' }}>
      {/* Top bar with back button and column adjust button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
        >
          返回
        </Button>
        <Badge count={selectedCols.length} offset={[-4, 4]} size="small" color="#533afd">
          <Button
            icon={<EditOutlined />}
            onClick={handleOpenDrawer}
          >
            调整数据列
          </Button>
        </Badge>
      </div>

      {/* Column Selection Drawer */}
      <Drawer
        title="调整数据列"
        placement="left"
        width={360}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => setDrawerOpen(false)}>取消</Button>
            <Button type="primary" onClick={handleApplyColumns}>
              应用更改
            </Button>
          </div>
        }
      >
        {columnsLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : (
          <div>
            {/* Column checkboxes */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>选择列</span>
                <span style={{ fontSize: 12, color: '#999' }}>
                  已选 {selectedCols.length} / {allColumns.length}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <Button
                  size="small"
                  onClick={() => setSelectedCols(allColumns.map(c => c.name))}
                >
                  全选
                </Button>
                <Button
                  size="small"
                  onClick={() => setSelectedCols([])}
                >
                  清空
                </Button>
              </div>
              <Checkbox.Group
                value={selectedCols}
                onChange={(vals) => setSelectedCols(vals as string[])}
                style={{ width: '100%' }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {allColumns.map((col) => {
                    const dtype = col.dtype || 'unknown';
                    const tagColor = TYPE_TAG_COLORS[dtype] || TYPE_TAG_COLORS['text'];
                    return (
                      <div
                        key={col.name}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '6px 8px', borderRadius: 6,
                          background: selectedCols.includes(col.name) ? '#f0f5ff' : '#fafafa',
                          border: `1px solid ${selectedCols.includes(col.name) ? '#d6e4ff' : '#f0f0f0'}`,
                        }}
                      >
                        <Checkbox value={col.name} style={{ flex: 1 }}>
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{col.name}</span>
                        </Checkbox>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Tag color={tagColor} style={{ fontSize: 10, lineHeight: '18px', margin: 0 }}>
                            {dtype}
                          </Tag>
                          {col.mean !== undefined && (
                            <Tooltip title={`均值: ${col.mean?.toFixed(2)}, 标准差: ${col.std?.toFixed(2)}`}>
                              <span style={{ fontSize: 10, color: '#999', cursor: 'help' }}>
                                x̄={col.mean?.toFixed(1)}
                              </span>
                            </Tooltip>
                          )}
                          {col.unique !== undefined && (col.mean === undefined) && (
                            <span style={{ fontSize: 10, color: '#999' }}>
                              {col.unique}类
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Checkbox.Group>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            {/* Axis selectors */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>轴与分组变量</div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>X 轴</div>
                <Select
                  allowClear
                  placeholder="选择 X 轴列"
                  value={xAxis || undefined}
                  onChange={(val) => setXAxis(val || '')}
                  options={colOptions}
                  style={{ width: '100%' }}
                  size="small"
                />
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Y 轴</div>
                <Select
                  allowClear
                  placeholder="选择 Y 轴列"
                  value={yAxis || undefined}
                  onChange={(val) => setYAxis(val || '')}
                  options={colOptions}
                  style={{ width: '100%' }}
                  size="small"
                />
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>分组变量</div>
                <Select
                  allowClear
                  placeholder="选择分组列"
                  value={groupBy || undefined}
                  onChange={(val) => setGroupBy(val || '')}
                  options={colOptions}
                  style={{ width: '100%' }}
                  size="small"
                />
              </div>
            </div>
          </div>
        )}
      </Drawer>

      {/* AI Top 3 推荐 */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ color: '#061b31', fontSize: 20, fontWeight: 600, marginBottom: 16 }}>
          AI 智能匹配图表
          <Tag color="purple" style={{ marginLeft: 8, fontSize: 12 }}>只推荐三个</Tag>
        </h2>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
            {top3.map((rec, index) => (
              <Card
                key={rec.chart_type}
                hoverable
                onClick={() => handleSelectChart(rec)}
                style={{
                  borderColor: index === 0 ? '#533afd' : undefined,
                  borderWidth: index === 0 ? 2 : 1,
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    width: 60, height: 60, borderRadius: '50%',
                    background: index === 0 ? 'linear-gradient(135deg, #533afd 0%, #6b54fd 100%)' : '#f0f2f5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 12px', fontSize: 24,
                    color: index === 0 ? '#fff' : '#533afd',
                  }}>
                    {iconMap[rec.chart_type] || <BarChartOutlined />}
                  </div>
                  <h3 style={{ fontSize: 16, fontWeight: 600, color: '#061b31', marginBottom: 4 }}>
                    {rec.display_name}
                  </h3>
                  <Tag color="blue" style={{ marginBottom: 8 }}>
                    匹配度 {Math.round(rec.confidence * 100)}%
                  </Tag>
                  <p style={{ fontSize: 12, color: '#666', margin: 0 }}>{rec.reason}</p>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* 对话输入 */}
      <div style={{
        background: '#fff', borderRadius: 10, padding: '12px 16px',
        marginBottom: 32, display: 'flex', gap: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        <Input
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          placeholder="输入您想设计的图表，例如：我想看一个展示各类别占比的图"
          onPressEnter={handleChat}
          size="large"
          style={{ flex: 1 }}
        />
        <Button type="primary" icon={<SendOutlined />} size="large" onClick={handleChat}>
          发送
        </Button>
      </div>

      {/* ===== MECE 分组展示 ===== */}
      {categorizedCharts.map((cat) => cat.charts.length > 0 && (
        <div key={cat.key} style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
            <div style={{ width: 4, height: 22, borderRadius: 2, background: cat.color }} />
            <h2 style={{ color: '#061b31', fontSize: 18, fontWeight: 600, margin: 0 }}>
              {cat.label}
              <Tag color={cat.color} style={{ marginLeft: 8, fontSize: 12, borderColor: cat.color, color: cat.color, background: `${cat.color}10` }}>{cat.charts.length} 种</Tag>
            </h2>
          </div>
          <p style={{ color: '#999', fontSize: 13, marginBottom: 16, paddingLeft: 16 }}>{cat.desc}</p>

          {cat.layout === 'grid' ? (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cat.gridCols}, 1fr)`, gap: 14 }}>
              {cat.charts.map((chart) => (
                <Tooltip key={chart.chart_type} title={chart.reason} placement="top">
                  <div
                    onClick={() => handleSelectChart(chart)}
                    style={{
                      aspectRatio: '1 / 1',
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', gap: 8,
                      background: '#fff', border: '2px solid #e8e8e8',
                      borderRadius: 12, cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      position: 'relative',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = cat.color; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 6px 20px ${cat.color}20`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e8e8e8'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; }}
                  >
                    <div style={{ fontSize: 32, color: cat.color }}>{iconMap[chart.chart_type] || <BarChartOutlined />}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#333' }}>{chart.display_name}</div>
                  </div>
                </Tooltip>
              ))}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cat.gridCols}, 1fr)`, gap: 16 }}>
              {cat.charts.map((chart) => (
                <Tooltip key={chart.chart_type} title={chart.reason} placement="top">
                  <div
                    onClick={() => handleSelectChart(chart)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 16,
                      background: '#fff', border: '2px solid #e8e8e8',
                      borderRadius: 12, cursor: 'pointer', padding: '16px 20px',
                      transition: 'all 0.25s ease',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = cat.color; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 6px 20px ${cat.color}20`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e8e8e8'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; }}
                  >
                    <div style={{
                      width: 48, height: 48, borderRadius: 10,
                      background: `linear-gradient(135deg, ${cat.color} 0%, ${cat.color}99 100%)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 22, color: '#fff', flexShrink: 0,
                    }}>
                      {iconMap[chart.chart_type] || <BarChartOutlined />}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#061b31' }}>{chart.display_name}</div>
                      <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{chart.reason}</div>
                    </div>
                  </div>
                </Tooltip>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* 不适用于当前数据 */}
      {incompatibleCharts.length > 0 && (
        <div>
          <h2 style={{ color: '#bbb', fontSize: 15, fontWeight: 500, marginBottom: 12 }}>
            不适用于当前数据
            <Tag color="default" style={{ marginLeft: 8, fontSize: 11 }}>{incompatibleCharts.length} 种</Tag>
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10 }}>
            {incompatibleCharts.map((chart) => (
              <Tooltip key={chart.chart_type} title={`需要：${chart.require}`} placement="top">
                <div
                  onClick={() => handleSelectChart(chart)}
                  style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 6,
                    background: '#fafafa', border: '1px dashed #e0e0e0',
                    borderRadius: 8, cursor: 'not-allowed',
                    opacity: 0.45, padding: '12px 4px',
                  }}
                >
                  <div style={{ fontSize: 24, color: '#ccc' }}>{iconMap[chart.chart_type] || <BarChartOutlined />}</div>
                  <div style={{ fontSize: 11, color: '#aaa', textAlign: 'center' }}>{chart.display_name}</div>
                </div>
              </Tooltip>
            ))}
          </div>
        </div>
      )}

      {/* USL/LSL input modal for capability histogram */}
      <Modal
        title="输入规格限 (USL / LSL)"
        open={specModalOpen}
        onOk={handleSpecConfirm}
        onCancel={() => setSpecModalOpen(false)}
        okText="生成图表"
        cancelText="取消"
      >
        <p style={{ color: '#666', marginBottom: 16 }}>
          输入上规格限和下规格限以计算过程能力指数 (Cp/Cpk)。留空则只显示直方图和正态拟合曲线。
        </p>
        <div style={{ display: 'flex', gap: 24, marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>USL (上规格限)</div>
            <InputNumber
              style={{ width: '100%' }}
              placeholder="例如：10.5"
              value={uslValue}
              onChange={(v) => setUslValue(v)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>LSL (下规格限)</div>
            <InputNumber
              style={{ width: '100%' }}
              placeholder="例如：9.5"
              value={lslValue}
              onChange={(v) => setLslValue(v)}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SmartChartTypeSelect;
